import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip, Modal, message, Dropdown } from 'antd';

import { aiApi } from '../../modules/api';
import './generate-panel.css';

const DEFAULT_SIZE = '1024x1024';
const DEFAULT_PROMPT_MAX_CHARACTERS = 8000;

const FALLBACK_SIZE_OPTIONS = [
  { value: '1024x1024', label: 'Aspect ratio · 1:1 (1024x1024)' },
  { value: '1152x768', label: 'Aspect ratio · 3:2 (1152x768)' },
  { value: '1280x720', label: 'Aspect ratio · 16:9 (1280x720)' },
  { value: '720x1280', label: 'Aspect ratio · 9:16 (720x1280)' }
];

const SEEDREAM_MODEL_VALUES = new Set([
  'seedream-5-0-pro-260628',
  'seedream-5-0-260128',
  'seedream-4-5-251128',
  'seedream-4-0-250828',
]);
const ALI_REFERENCE_MODEL_VALUES = new Set([
  'qwen-image-3-0-pro',
  'qwen-image-3.0-pro',
  'wan2-7-image',
  'wan2.7-image',
  'wan2-7-image-pro',
  'wan2.7-image-pro',
  'qwen-image-2-0',
  'qwen-image-2.0',
  'qwen-image-2-0-pro',
  'qwen-image-2.0-pro',
]);
const ALI_SINGLE_REFERENCE_MODEL_VALUES = new Set(ALI_REFERENCE_MODEL_VALUES);
const CHARACTER_SWAP_DEFAULT_MODEL_VALUE = 'qwen-image-3-0-pro';
const CHARACTER_SWAP_MODEL_VALUES = new Set([
  'seedream-4-5-251128',
  'seedream-5-0-pro-260628',
  'seedream-5-0-260128',
  ...ALI_REFERENCE_MODEL_VALUES,
]);
const CHARACTER_SWAP_REFERENCE_LIMIT = 6;
const DEFAULT_REFERENCE_UPLOAD_LIMIT = 3;
const MODEL_OPTIONS = [
  { value: 'qwen-image-3-0-pro', label: 'Qwen-Image 3.0 Pro', creditCost: 5, disabled: false },
  { value: 'wan2-7-image', label: 'Wan2.7 Image', disabled: false },
  { value: 'wan2-7-image-pro', label: 'Wan2.7 Image Pro', disabled: false },
  { value: 'qwen-image-2-0', label: 'Qwen-Image 2.0', disabled: false },
  { value: 'qwen-image-2-0-pro', label: 'Qwen-Image 2.0 Pro', disabled: false },
  { value: 'flux-2-pro', label: 'FLUX-2 Pro', disabled: false },
  { value: 'flux-2-max', label: 'FLUX-2 Max', disabled: false },
  { value: 'flux-2-klein-9b', label: 'FLUX-2 Klein 9B', disabled: false },
  { value: 'flux-2-klein-4b', label: 'FLUX-2 Klein 4B', disabled: false },
  { value: 'flux-1-1-pro', label: 'FLUX-1.1 Pro', disabled: false },
  { value: 'flux-1-schnell', label: 'FLUX-1', disabled: false },
  { value: 'qwen-image-max', label: 'Qwen-Image-Max', disabled: false },
  { value: 'bria-3.2', label: 'Bria 3.2', disabled: false },
  { value: 'sdxl-turbo', label: 'SDXL Turbo', disabled: false },
  { value: 'p-image', label: 'p-image', disabled: false },
];

const STYLE_OPTIONS = [
  { value: 'cinematic', label: 'Cinematic, dramatic lighting' },
  { value: 'analog', label: 'Analog film, grainy, soft focus' },
  { value: 'watercolor', label: 'Watercolor illustration, soft washes' },
  { value: 'line-art', label: 'Clean line art, minimal shading' },
  { value: 'digital-paint', label: 'Digital painting, rich brush strokes' },
  { value: 'studio', label: 'Studio photography, crisp lighting' },
  { value: 'noir', label: 'Noir, high contrast, moody shadows' },
  { value: 'fantasy', label: 'High-fantasy, magical atmosphere' },
  { value: '3d', label: '3D render, high detail, subsurface scattering' },
  { value: 'poster', label: 'Graphic poster, bold colors, flat shapes' },
];

const normalizeModelValue = (value) => String(value || '').trim().toLowerCase();
const normalizeSizeValue = (value) => String(value || '').trim().toLowerCase();

const isSeedreamModel = (value) => SEEDREAM_MODEL_VALUES.has(normalizeModelValue(value));
const isAliReferenceModel = (value) => ALI_REFERENCE_MODEL_VALUES.has(normalizeModelValue(value));
const isReferenceImageModel = (value) => isSeedreamModel(value) || isAliReferenceModel(value);
const isCharacterSwapModel = (value) => CHARACTER_SWAP_MODEL_VALUES.has(normalizeModelValue(value));
const isSingleReferenceModel = (value) => ALI_SINGLE_REFERENCE_MODEL_VALUES.has(normalizeModelValue(value));
const getStyleReferenceUploadLimit = (modelValue, hasCharacterReference = false) => {
  if (isSingleReferenceModel(modelValue)) {
    return hasCharacterReference ? 1 : 2;
  }
  return DEFAULT_REFERENCE_UPLOAD_LIMIT;
};
const getCharacterSwapReferenceLimit = (modelValue) => (
  isSingleReferenceModel(modelValue) ? 1 : CHARACTER_SWAP_REFERENCE_LIMIT
);

const findGenerationModel = (generationModels, modelValue) => {
  const normalizedTarget = normalizeModelValue(modelValue);
  if (!normalizedTarget || !Array.isArray(generationModels)) {
    return null;
  }

  return generationModels.find((item) => {
    if (!item) {
      return false;
    }

    return [item.modelKey, item.providerModel, item.label, item.id]
      .filter(Boolean)
      .map((candidate) => normalizeModelValue(candidate))
      .includes(normalizedTarget);
  }) || null;
};

const getModelSizeOptions = (generationModels, modelValue) => {
  const matchedModel = findGenerationModel(generationModels, modelValue);
  const options = matchedModel && Array.isArray(matchedModel.supportedSizes)
    ? matchedModel.supportedSizes
        .filter((item) => item && item.value && item.label)
        .map((item) => ({
          value: item.value,
          label: item.label,
          promptSuffix: item.promptSuffix || '',
        }))
    : [];

  return options.length ? options : FALLBACK_SIZE_OPTIONS;
};

const getModelDefaultSize = (generationModels, modelValue, sizeOptions) => {
  const matchedModel = findGenerationModel(generationModels, modelValue);
  const modelDefaultSize = matchedModel && matchedModel.defaultSize
    ? String(matchedModel.defaultSize)
    : '';
  if (modelDefaultSize && sizeOptions.some((option) => option.value === modelDefaultSize)) {
    return modelDefaultSize;
  }
  return (sizeOptions[0] || {}).value || DEFAULT_SIZE;
};

const getModelPromptMaxCharacters = (generationModels, modelValue) => {
  const matchedModel = findGenerationModel(generationModels, modelValue);
  const maxCharacters = Number(matchedModel && matchedModel.promptMaxCharacters);
  return Number.isFinite(maxCharacters) && maxCharacters > 0
    ? Math.trunc(maxCharacters)
    : DEFAULT_PROMPT_MAX_CHARACTERS;
};

const getCharacterSwapBlockedMessage = (reason) => {
  const normalized = String(reason || '').toLowerCase();
  if (normalized.includes('sensitive information')) {
    return 'This character cannot be used in Character Swap because the provider blocked swap output for likely public-figure identity.';
  }
  return 'This character cannot be used in Character Swap right now.';
};

const getCharacterPreviewAsset = (character) => {
  if (!character) {
    return null;
  }

  const candidates = [
    character.coverAvatar,
    ...(Array.isArray(character.avatarImages) ? character.avatarImages : []),
    ...(Array.isArray(character.runtimeReferenceImages) ? character.runtimeReferenceImages : []),
    ...(Array.isArray(character.uploadedSourceImages) ? character.uploadedSourceImages : []),
  ];

  return candidates.find((asset) => asset && asset.url) || null;
};

const buildCharacterSwapPrompt = ({ basePrompt, characterImageCount }) => {
  const trimmedPrompt = String(basePrompt || '').trim();
  const referenceImageNumbers = Array.from(
    { length: Math.max(characterImageCount, 1) },
    (_, index) => index + 2,
  );
  let subjectReference = 'the person shown in Image 2';

  if (referenceImageNumbers.length === 2) {
    subjectReference = 'the same person shown in Images 2 and 3';
  } else if (referenceImageNumbers.length > 2) {
    const lastImageNumber = referenceImageNumbers[referenceImageNumbers.length - 1];
    subjectReference = `the same person shown consistently in Images 2 to ${lastImageNumber}`;
  }

  const promptParts = [
    `Replace the subject in Image 1 with ${subjectReference}.`,
    'Keep the composition, pose, camera angle, lighting, background, framing, and wardrobe of Image 1 unchanged.',
    'Make the result photorealistic, natural, and identity-consistent.',
  ];

  if (trimmedPrompt) {
    promptParts.push(`Additional instructions: ${trimmedPrompt}`);
  }

  return promptParts.join(' ');
};

const formatModelCredits = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) {
    return '';
  }
  return `(${amount.toLocaleString()} credit${amount === 1 ? '' : 's'})`;
};

const enhancePromptText = (value, maxCharacters = DEFAULT_PROMPT_MAX_CHARACTERS) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return '';
  }
  const safeMaxCharacters =
    Number.isFinite(Number(maxCharacters)) && Number(maxCharacters) > 0
      ? Math.trunc(Number(maxCharacters))
      : DEFAULT_PROMPT_MAX_CHARACTERS;

  if (trimmed.length >= safeMaxCharacters) {
    return trimmed;
  }

  const lower = trimmed.toLowerCase();
  const additions = [];
  if (!/(lighting|light|backlit|cinematic|soft glow)/i.test(lower)) {
    additions.push('cinematic lighting');
  }
  if (!/(composition|framing|angle|wide shot|close-up|portrait|landscape)/i.test(lower)) {
    additions.push('strong composition');
  }
  if (!/(detail|highly detailed|texture|realistic|photorealistic)/i.test(lower)) {
    additions.push('highly detailed textures');
  }
  if (!/(mood|atmosphere|dreamlike|dramatic|serene|surreal)/i.test(lower)) {
    additions.push('immersive atmosphere');
  }

  if (!additions.length) {
    return trimmed;
  }

  const suffixParts = [...additions, 'polished professional finish'];
  let enhanced = trimmed;

  suffixParts.forEach((part) => {
    const separator = enhanced === trimmed ? '. ' : ', ';
    const ending = part === suffixParts[suffixParts.length - 1] ? '.' : '';
    const candidate = `${enhanced}${separator}${part}${ending}`;
    if (candidate.length <= safeMaxCharacters) {
      enhanced = candidate;
    }
  });

  return enhanced;
};

const GenerateSparklesIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="generate-btn__icon"
  >
    <path d="M9 3l1.5 3.5L14 8l-3.5 1.5L9 13l-1.5-3.5L4 8l3.5-1.5L9 3z" />
    <path d="M18 12l.8 1.9L21 15l-2.2 1.1L18 18l-.8-1.9L15 15l2.2-1.1L18 12z" />
    <path d="M5 14l.9 2.1L8 17l-2.1.9L5 20l-.9-2.1L2 17l2.1-.9L5 14z" />
  </svg>
);

const GeneratePanel = ({
  promptValue,
  promptVersion,
  onPromptChange,
  onGenerate,
  isGenerating,
  modelValue,
  onModelChange,
  sizeValue,
  onSizeChange,
  referenceImageUrls,
  generationModels,
  selectedCharacter,
  activateCharacterSwap,
  onOpenCharacterPicker,
  onCreateCharacter,
  onClearCharacterSelection,
}) => {
  const [prompt, setPrompt] = useState(promptValue || '');
  const [selectedModel, setSelectedModel] = useState(modelValue || MODEL_OPTIONS[0].value);
  const [selectedSize, setSelectedSize] = useState(sizeValue || DEFAULT_SIZE);
  const [describeModalOpen, setDescribeModalOpen] = useState(false);
  const [describeFile, setDescribeFile] = useState(null);
  const [describeError, setDescribeError] = useState('');
  const [isDescribeLoading, setIsDescribeLoading] = useState(false);
  const [describePreview, setDescribePreview] = useState('');
  const [describeResult, setDescribeResult] = useState('');
  const [isEnhancingPrompt, setIsEnhancingPrompt] = useState(false);
  const [enhanceLockedPrompt, setEnhanceLockedPrompt] = useState(() => (
    String(promptValue || '').trim()
  ));
  const [isDragActive, setIsDragActive] = useState(false);
  const [isEditDragActive, setIsEditDragActive] = useState(false);
  const promptTextareaRef = useRef(null);
  const internalPromptUpdateRef = useRef(null);
  const previousPromptVersionRef = useRef(promptVersion);
  const editFileInputRef = useRef(null);
  const describeFileInputRef = useRef(null);
  const [editImageFiles, setEditImageFiles] = useState([]);
  const [editImagePreviews, setEditImagePreviews] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [styleReferenceActive, setStyleReferenceActive] = useState(false);
  const [characterSwapActive, setCharacterSwapActive] = useState(false);
  const [imageMentionOpen, setImageMentionOpen] = useState(false);
  const [activeImageMentionIndex, setActiveImageMentionIndex] = useState(0);
  const lastNonEditModelRef = useRef(
    modelValue ? modelValue : MODEL_OPTIONS[0].value,
  );
  const defaultModelValue = MODEL_OPTIONS[0].value;

  useEffect(() => {
    const nextPrompt = promptValue || '';
    const isInternalEcho =
      internalPromptUpdateRef.current === nextPrompt
      && previousPromptVersionRef.current === promptVersion;

    setPrompt(nextPrompt);
    if (isInternalEcho) {
      internalPromptUpdateRef.current = null;
    } else {
      setEnhanceLockedPrompt(String(nextPrompt || '').trim());
    }
    previousPromptVersionRef.current = promptVersion;
  }, [promptValue, promptVersion]);

  useEffect(() => {
    setSelectedModel(modelValue || MODEL_OPTIONS[0].value);
  }, [modelValue]);

  useEffect(() => {
    setSelectedSize(sizeValue || DEFAULT_SIZE);
  }, [sizeValue]);

  useEffect(
    () => () => {
      if (describePreview) {
        URL.revokeObjectURL(describePreview);
      }
    },
    [describePreview]
  );

  useEffect(() => {
    const preventFileNavigation = (event) => {
      const types = event.dataTransfer && Array.from(event.dataTransfer.types || []);
      if (types.includes('Files')) {
        event.preventDefault();
      }
    };

    window.addEventListener('dragover', preventFileNavigation);
    window.addEventListener('drop', preventFileNavigation);

    return () => {
      window.removeEventListener('dragover', preventFileNavigation);
      window.removeEventListener('drop', preventFileNavigation);
    };
  }, []);

  const previousReferenceImagesRef = useRef(null);

  const clearEditImages = () => {
    editImagePreviews.forEach((preview) => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    });
    setEditImageFiles([]);
    setEditImagePreviews([]);
  };

  useEffect(() => {
    if (!Array.isArray(referenceImageUrls)) {
      return;
    }
    const filtered = referenceImageUrls.filter(Boolean).slice(0, 3);
    if (!isReferenceImageModel(selectedModel)) {
      if (editImagePreviews.length || editImageFiles.length) {
        clearEditImages();
      }
      if (styleReferenceActive) {
        setStyleReferenceActive(false);
      }
      if (characterSwapActive) {
        setCharacterSwapActive(false);
      }
      return;
    }
    if (characterSwapActive) {
      return;
    }
    const previous = previousReferenceImagesRef.current || [];
    if (
      previous.length === filtered.length &&
      previous.every((value, index) => value === filtered[index])
    ) {
      return;
    }
    previousReferenceImagesRef.current = filtered;
    if (!filtered.length) {
      if (!previous.length) {
        return;
      }
      clearEditImages();
      setStyleReferenceActive(false);
      return;
    }
    setStyleReferenceActive(true);
    setEditImagePreviews((previous) => {
      previous.forEach((preview) => {
        if (preview && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview);
        }
      });
      return filtered;
    });
    if (editImageFiles.length) {
      setEditImageFiles([]);
    }
  }, [
    referenceImageUrls,
    editImageFiles.length,
    editImagePreviews.length,
    selectedModel,
    styleReferenceActive,
    characterSwapActive,
  ]);

  useEffect(() => {
    const referenceImageModel = isReferenceImageModel(selectedModel);
    if (!referenceImageModel && (editImagePreviews.length || editImageFiles.length)) {
      clearEditImages();
    }
    if (!referenceImageModel && styleReferenceActive) {
      setStyleReferenceActive(false);
    }
    if (!isCharacterSwapModel(selectedModel) && characterSwapActive) {
      setCharacterSwapActive(false);
      clearEditImages();
    }
    if (selectedModel && !styleReferenceActive) {
      lastNonEditModelRef.current = selectedModel;
    }
  }, [
    selectedModel,
    editImagePreviews,
    editImageFiles,
    styleReferenceActive,
    characterSwapActive,
    onModelChange,
  ]);

  useEffect(() => {
    if (!activateCharacterSwap || !selectedCharacter) {
      return;
    }

    if (!isCharacterSwapModel(selectedModel)) {
      setSelectedModel(CHARACTER_SWAP_DEFAULT_MODEL_VALUE);
      if (onModelChange) {
        onModelChange(CHARACTER_SWAP_DEFAULT_MODEL_VALUE);
      }
    }

    if (styleReferenceActive) {
      setStyleReferenceActive(false);
      clearEditImages();
    }

    setCharacterSwapActive(true);
  }, [
    activateCharacterSwap,
    onModelChange,
    selectedCharacter,
    selectedModel,
    styleReferenceActive,
  ]);

  const updatePrompt = (nextPrompt, options = {}) => {
    setPrompt(nextPrompt);
    internalPromptUpdateRef.current = nextPrompt;
    setEnhanceLockedPrompt(options.markAsEnhanced ? String(nextPrompt || '').trim() : '');

    if (onPromptChange) {
      onPromptChange(nextPrompt);
    }
  };

  const closeImageMentionMenu = () => {
    setImageMentionOpen(false);
    setActiveImageMentionIndex(0);
  };

  const resolveEnhanceTaskType = () => {
    if (characterSwapActive) {
      return 'character_swap';
    }
    if (styleReferenceActive) {
      return 'style_reference';
    }
    const fallbackReferenceUrls = editImagePreviews.filter((preview) => !preview.startsWith('blob:'));
    const resolvedReferenceUrls = Array.isArray(referenceImageUrls) && referenceImageUrls.length
      ? referenceImageUrls
      : fallbackReferenceUrls;
    if (editImageFiles.length || resolvedReferenceUrls.length) {
      return 'edit';
    }
    return 'generate';
  };

  const handleEnhancePrompt = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      message.info('Add a prompt first.');
      return;
    }
    if (trimmedPrompt.length >= selectedPromptMaxCharacters) {
      message.info(`Prompt is already at the ${selectedModelLabel} character limit.`);
      return;
    }

    setIsEnhancingPrompt(true);
    try {
      const response = await aiApi.enhancePrompt({
        prompt: trimmedPrompt,
        model: selectedModel,
        size: selectedSize,
        taskType: resolveEnhanceTaskType(),
      });
      const enhanced = String(response && response.data && response.data.prompt
        ? response.data.prompt
        : '').trim();
      if (!enhanced) {
        throw new Error('EMPTY_ENHANCED_PROMPT');
      }
      if (enhanced === trimmedPrompt) {
        setEnhanceLockedPrompt(trimmedPrompt);
        message.info('Prompt already has enough detail.');
        return;
      }
      updatePrompt(enhanced, { markAsEnhanced: true });
      message.success('Prompt enhanced.');
    } catch (error) {
      const enhanced = enhancePromptText(trimmedPrompt, selectedPromptMaxCharacters);
      if (enhanced && enhanced !== trimmedPrompt) {
        updatePrompt(enhanced, { markAsEnhanced: true });
        message.warning('Prompt enhanced locally because the LLM enhancer was unavailable.');
      } else {
        const responseMessage = error
          && error.response
          && error.response.data
          && error.response.data.message;
        message.error(responseMessage || 'Unable to enhance prompt.');
      }
    } finally {
      setIsEnhancingPrompt(false);
    }
  };

  const handleGenerateClick = () => {
    const trimmedPrompt = prompt.trim();

    if (!trimmedPrompt && !characterSwapActive) {
      return;
    }
    const referenceImageModel = isReferenceImageModel(selectedModel);
    const characterSwapModel = isCharacterSwapModel(selectedModel);
    const selectedCharacterReferenceUrl = !characterSwapActive
      && styleReferenceActive
      && characterSwapModel
      && selectedCharacterPreviewAsset
      && selectedCharacterPreviewAsset.url
      ? selectedCharacterPreviewAsset.url
      : null;
    const fallbackReferenceUrls = editImagePreviews.filter((preview) => !preview.startsWith('blob:'));
    const resolvedReferenceUrls = Array.isArray(referenceImageUrls) && referenceImageUrls.length
      ? referenceImageUrls
      : fallbackReferenceUrls;
    const hasImageReference = referenceImageModel
      && !characterSwapActive
      && styleReferenceActive
      && (editImageFiles.length || resolvedReferenceUrls.length || selectedCharacterReferenceUrl);
    if (referenceImageModel && styleReferenceActive && !editImageFiles.length && !resolvedReferenceUrls.length && !selectedCharacterReferenceUrl) {
      message.error(`Add a reference image to use ${selectedModelLabel}.`);
      return;
    }

    const styleMatch = selectedStyle
      ? STYLE_OPTIONS.find((item) => item.value === selectedStyle)
      : null;
    const styleSuffix = styleMatch ? styleMatch.label : '';
    const finalPrompt = [trimmedPrompt, styleSuffix]
      .filter(Boolean)
      .join(' ');

    if (onGenerate) {
      const payload = {
        prompt: finalPrompt,
        model: selectedModel,
        size: selectedSize
      };
      if (characterSwapActive) {
        if (!characterSwapModel) {
          message.info('Character Swap is available only for supported face swap models.');
          return;
        }
        if (!selectedCharacter) {
          message.error('Select a reference character to continue.');
          return;
        }
        if (selectedCharacter.swapStatus === 'blocked') {
          message.error(getCharacterSwapBlockedMessage(selectedCharacter.swapBlockedReason));
          return;
        }
        if (editImageFiles.length !== 1) {
          message.error('Upload exactly one reference image to continue.');
          return;
        }
        const characterReferenceLimit = getCharacterSwapReferenceLimit(selectedModel);
        const runtimeReferenceUrls = Array.from(
          new Set([
            ...((selectedCharacter.runtimeReferenceImages || [])
              .map((item) => item && item.url)
              .filter(Boolean)),
          ]),
        ).slice(0, characterReferenceLimit);
        const fallbackReferenceUrls = Array.from(
          new Set([
            ...((selectedCharacter.uploadedSourceImages || [])
              .map((item) => item && item.url)
              .filter(Boolean)),
          ]),
        ).slice(0, characterReferenceLimit);
        const swapReferenceUrls = (
          runtimeReferenceUrls.length ? runtimeReferenceUrls : fallbackReferenceUrls
        );

        if (!swapReferenceUrls.length) {
          message.error('This character does not have a usable Character ID yet.');
          return;
        }

        payload.prompt = buildCharacterSwapPrompt({
          basePrompt: finalPrompt,
          characterImageCount: swapReferenceUrls.length,
        });
        payload.displayPrompt = 'Character Swap';
        payload.historyPrompt = 'Character Swap';
        payload.taskType = 'character_swap';
        payload.imageFiles = editImageFiles.slice(0, 1);
        payload.imageUrls = swapReferenceUrls;
        payload.useImageReference = true;
        onGenerate(payload);
        return;
      }
      if (hasImageReference) {
        const hasCharacterReference = Boolean(selectedCharacterReferenceUrl);
        const styleReferenceUploadLimit = getStyleReferenceUploadLimit(selectedModel, hasCharacterReference);
        const limitedEditImageFiles = editImageFiles.slice(0, styleReferenceUploadLimit);
        const limitedResolvedReferenceUrls = resolvedReferenceUrls.slice(0, styleReferenceUploadLimit);
        const orderedImageUrls = [
          selectedCharacterReferenceUrl,
          ...(!limitedEditImageFiles.length ? limitedResolvedReferenceUrls : []),
        ].filter(Boolean);
        if (selectedCharacterReferenceUrl) {
          payload.taskType = 'style_reference';
        }
        payload.imageFiles = limitedEditImageFiles;
        if (orderedImageUrls.length) {
          payload.imageUrls = orderedImageUrls;
        }
        payload.useImageReference = true;
      }
      onGenerate(payload);
    }
  };

  const handleModelSelect = (value) => {
    setSelectedModel(value);
    const nextIsReferenceImageModel = isReferenceImageModel(value);
    const nextIsCharacterSwap = isCharacterSwapModel(value);
    if (!nextIsReferenceImageModel) {
      if (styleReferenceActive) {
        setStyleReferenceActive(false);
      }
      clearEditImages();
    }
    if (!nextIsCharacterSwap && characterSwapActive) {
      setCharacterSwapActive(false);
      clearEditImages();
    }
    if (!styleReferenceActive) {
      lastNonEditModelRef.current = value;
    }
    if (onModelChange) {
      onModelChange(value);
    }
  };

  const handleSizeSelect = (value) => {
    setSelectedSize(value);
    if (onSizeChange) {
      onSizeChange(value);
    }
  };

  const availableSizeOptions = useMemo(
    () => getModelSizeOptions(generationModels, selectedModel),
    [generationModels, selectedModel],
  );

  const defaultSizeValue = useMemo(
    () => getModelDefaultSize(generationModels, selectedModel, availableSizeOptions),
    [availableSizeOptions, generationModels, selectedModel],
  );

  useEffect(() => {
    const normalizedSelectedSize = normalizeSizeValue(selectedSize);
    const isCurrentSizeValid = availableSizeOptions.some(
      (option) => normalizeSizeValue(option.value) === normalizedSelectedSize,
    );

    if (isCurrentSizeValid) {
      return;
    }

    const nextSize = defaultSizeValue || DEFAULT_SIZE;
    setSelectedSize(nextSize);
    if (onSizeChange) {
      onSizeChange(nextSize);
    }
  }, [availableSizeOptions, defaultSizeValue, onSizeChange, selectedSize]);

  const handleEditFileSelect = (files) => {
    if (!files || !files.length) {
      editImagePreviews.forEach((preview) => {
        URL.revokeObjectURL(preview);
      });
      setEditImageFiles([]);
      setEditImagePreviews([]);
      return;
    }

    const incoming = Array.from(files);
    const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;
    const referenceImageModel = isReferenceImageModel(selectedModel);
    const maxUploadCount = getStyleReferenceUploadLimit(selectedModel, hasCharacterStyleReference);

    if (!referenceImageModel || characterSwapActive || !styleReferenceActive) {
      const selected = incoming.slice(0, 1);
      const previews = selected.map((file) => URL.createObjectURL(file));
      editImagePreviews.forEach((preview) => {
        URL.revokeObjectURL(preview);
      });
      setEditImageFiles(selected);
      setEditImagePreviews(previews);
      return;
    }

    const existingMap = new Map(
      editImageFiles.map((file, index) => [fileKey(file), editImagePreviews[index]]),
    );
    const combined = [...editImageFiles, ...incoming];
    const seen = new Set();
    const unique = [];
    for (const file of combined) {
      const key = fileKey(file);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      unique.push(file);
      if (unique.length >= maxUploadCount) {
        break;
      }
    }

    const nextPreviews = unique.map((file) => {
      const existingPreview = existingMap.get(fileKey(file));
      return existingPreview || URL.createObjectURL(file);
    });

    editImagePreviews.forEach((preview) => {
      if (!nextPreviews.includes(preview)) {
        URL.revokeObjectURL(preview);
      }
    });

    setEditImageFiles(unique);
    setEditImagePreviews(nextPreviews);
  };

  const handleRemoveEditImage = (index) => {
    setEditImageFiles((previous) => previous.filter((_, idx) => idx !== index));
    setEditImagePreviews((previous) => {
      const next = previous.filter((_, idx) => idx !== index);
      if (previous[index]) {
        URL.revokeObjectURL(previous[index]);
      }
      return next;
    });
  };

  const handleGenerateReferenceFileSelect = (files) => {
    handleEditFileSelect(files);
  };

  const handleSelectCharacterReference = () => {
    if (!characterSwapModel) {
      message.info('Saved character references are available only for supported reference models.');
      return;
    }
    if (onOpenCharacterPicker) {
      onOpenCharacterPicker('reference');
    }
  };

  const handleClearCharacterReference = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (onClearCharacterSelection) {
      onClearCharacterSelection();
    }
  };

  const aspectRatioMenu = useMemo(
    () => ({
      items: availableSizeOptions.map((option) => ({
        key: option.value,
        label: option.label.includes('· ')
          ? option.label.split('· ')[1]
          : option.label,
        onClick: () => handleSizeSelect(option.value),
      })),
    }),
    [availableSizeOptions],
  );

  const availableModelOptions = useMemo(() => {
    if (!Array.isArray(generationModels) || !generationModels.length) {
      return MODEL_OPTIONS;
    }

    const mapped = generationModels
      .filter((item) => item && item.category !== 'edit' && item.isEnabled !== false)
      .filter((item) => item.provider !== 'byteplus' && !/seedream|byteplus/i.test(item.modelKey || ''))
      .map((item) => ({
        value: item.modelKey || item.id,
        label: item.label || item.modelKey || item.id,
        creditCost: Number(item.generationCreditCost) || 0,
        promptMaxCharacters: Number(item.promptMaxCharacters) || DEFAULT_PROMPT_MAX_CHARACTERS,
        disabled: item.isEnabled === false,
      }))
      .filter((item) => item.value);

    return mapped.length ? mapped : MODEL_OPTIONS;
  }, [generationModels]);

  const modelMenu = useMemo(
    () => ({
      items: availableModelOptions.map((option) => ({
        key: option.value,
        disabled: option.disabled,
        label: option.value === 'qwen-image-3-0-pro' ? `${option.label} · 5–9 credits` : (option.creditCost ? `${option.label} · ${formatModelCredits(option.creditCost)}` : option.label),
        onClick: () => handleModelSelect(option.value),
      })),
    }),
    [availableModelOptions, handleModelSelect],
  );

  const stylesMenu = useMemo(
    () => ({
      items: [
        {
          key: 'none',
          label: 'No style',
          onClick: () => setSelectedStyle(null),
        },
        ...STYLE_OPTIONS.map((option) => ({
          key: option.value,
          label: option.label,
          onClick: () => setSelectedStyle(option.value),
        })),
      ],
    }),
    [],
  );

  const handleStyleReferenceClick = () => {
    if (!isReferenceImageModel(selectedModel)) {
      message.info('Reference images are available only for Seedream and Ali reference models.');
      return;
    }
    if (styleReferenceActive) {
      setStyleReferenceActive(false);
      clearEditImages();
      if (onClearCharacterSelection) {
        onClearCharacterSelection();
      }
      return;
    }
    if (characterSwapActive) {
      setCharacterSwapActive(false);
      clearEditImages();
    }
    setStyleReferenceActive(true);
    if (selectedModel) {
      lastNonEditModelRef.current = selectedModel;
    }
    if (!selectedModel) {
      setSelectedModel(lastNonEditModelRef.current || defaultModelValue);
      if (onModelChange) {
        onModelChange(lastNonEditModelRef.current || defaultModelValue);
      }
    }
  };

  const handleCharacterSwapClick = () => {
    if (!isCharacterSwapModel(selectedModel)) {
      message.info('Character Swap is available only for supported face swap models.');
      return;
    }
    if (characterSwapActive) {
      setCharacterSwapActive(false);
      clearEditImages();
      return;
    }
    if (styleReferenceActive) {
      setStyleReferenceActive(false);
      clearEditImages();
    }
    setCharacterSwapActive(true);
  };

  const selectedModelLabel = useMemo(() => {
    const found = availableModelOptions.find((option) => option.value === selectedModel);
    if (found) {
      return found.label;
    }
    const fallback = MODEL_OPTIONS.find((option) => option.value === selectedModel) || MODEL_OPTIONS[0];
    return fallback.label;
  }, [availableModelOptions, selectedModel]);

  const selectedModelDisplayLabel = useMemo(() => {
    const found = availableModelOptions.find((option) => option.value === selectedModel);
    if (!found) {
      const fallback = MODEL_OPTIONS.find((option) => option.value === selectedModel) || MODEL_OPTIONS[0];
      const fallbackCredits = formatModelCredits(fallback.creditCost);
      return fallbackCredits ? `${fallback.label} · ${fallbackCredits}` : fallback.label;
    }
    const pixels = String(selectedSize || '').split(/[x*]/i).map(Number);
    const credits = selectedModel === 'qwen-image-3-0-pro'
      ? Math.max(found.creditCost || 5, pixels[0] * pixels[1] > 2250000 ? 9 : 5)
      : found.creditCost;
    const creditLabel = formatModelCredits(credits);
    return creditLabel ? `${found.label} · ${creditLabel}` : found.label;
  }, [availableModelOptions, selectedModel, selectedSize]);

  const selectedSizeLabel = useMemo(() => {
    const found = availableSizeOptions.find((option) => option.value === selectedSize);
    if (!found) {
      return selectedSize;
    }
    const parts = found.label.split('· ');
    return parts.length > 1 ? parts[1] : found.label;
  }, [availableSizeOptions, selectedSize]);

  const selectedSizePrefix = useMemo(() => {
    const found = availableSizeOptions.find((option) => option.value === selectedSize);
    if (!found || !found.label.includes('· ')) {
      return 'Size';
    }
    return found.label.split('· ')[0];
  }, [availableSizeOptions, selectedSize]);

  const trimmedPrompt = prompt.trim();
  const referenceImageModel = isReferenceImageModel(selectedModel);
  const characterSwapModel = isCharacterSwapModel(selectedModel);
  const selectedCharacterPreviewAsset = getCharacterPreviewAsset(selectedCharacter);
  const hasCharacterStyleReference = Boolean(
    characterSwapModel
      && styleReferenceActive
      && selectedCharacterPreviewAsset
      && selectedCharacterPreviewAsset.url,
  );
  const styleReferenceUploadLimit = getStyleReferenceUploadLimit(selectedModel, hasCharacterStyleReference);
  const imageMentionOptions = useMemo(() => {
    if (!referenceImageModel || characterSwapActive) {
      return [];
    }

    if (!styleReferenceActive) {
      return [];
    }

    const options = [];
    if (characterSwapModel && selectedCharacterPreviewAsset && selectedCharacterPreviewAsset.url) {
      options.push({
        number: 1,
        label: 'Image 1',
        previewUrl: selectedCharacterPreviewAsset.url,
        description: selectedCharacter.displayName || 'Character reference',
      });
    }

    editImagePreviews.filter(Boolean).forEach((previewUrl, index) => {
      const number = options.length + 1;
      options.push({
        number,
        label: `Image ${number}`,
        previewUrl,
        description: editImageFiles[index] ? editImageFiles[index].name : 'Reference image',
      });
    });

    return options;
  }, [
    characterSwapActive,
    characterSwapModel,
    editImageFiles,
    editImagePreviews,
    referenceImageModel,
    selectedCharacter,
    selectedCharacterPreviewAsset,
    styleReferenceActive,
  ]);

  const referenceImageSlots = useMemo(() => (
    Array.from({ length: styleReferenceUploadLimit }).map((_, index) => {
      const previewUrl = editImagePreviews[index] || '';
      return {
        number: index + 1,
        label: `Image ${index + 1}`,
        previewUrl,
        description: editImageFiles[index] ? editImageFiles[index].name : 'Reference image',
      };
    })
  ), [editImageFiles, editImagePreviews, styleReferenceUploadLimit]);
  const styleReferenceSelectedCount = (hasCharacterStyleReference ? 1 : 0) + editImagePreviews.length;
  const styleReferenceSummary = styleReferenceSelectedCount > 0
    ? `${styleReferenceSelectedCount} reference${styleReferenceSelectedCount === 1 ? '' : 's'} selected`
    : '';
  const baseModelCreditCost =
    (availableModelOptions.find((option) => option.value === selectedModel) || {}).creditCost
    || (MODEL_OPTIONS.find((option) => option.value === selectedModel) || {}).creditCost
    || 0;
  const selectedPixels = String(selectedSize || '').split(/[x*]/i).map(Number);
  const selectedModelCreditCost = selectedModel === 'qwen-image-3-0-pro'
    ? Math.max(baseModelCreditCost, selectedPixels[0] * selectedPixels[1] > 2250000 ? 9 : 5)
    : baseModelCreditCost;
  const selectedPromptMaxCharacters = getModelPromptMaxCharacters(generationModels, selectedModel);
  const promptOverLimit = trimmedPrompt.length > selectedPromptMaxCharacters;
  const enhancePromptLocked = Boolean(trimmedPrompt && enhanceLockedPrompt === trimmedPrompt);
  const canGenerate = characterSwapActive
    ? Boolean(
        !isGenerating
        && selectedCharacter
        && selectedCharacter.swapStatus !== 'blocked'
        && editImageFiles.length === 1,
      )
    : trimmedPrompt.length > 0 && !promptOverLimit && !isGenerating;

  useEffect(() => {
    if (!styleReferenceActive || characterSwapActive) {
      return;
    }
    if (
      editImageFiles.length <= styleReferenceUploadLimit
      && editImagePreviews.length <= styleReferenceUploadLimit
    ) {
      return;
    }

    editImagePreviews.slice(styleReferenceUploadLimit).forEach((preview) => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    });
    setEditImageFiles((previous) => previous.slice(0, styleReferenceUploadLimit));
    setEditImagePreviews((previous) => previous.slice(0, styleReferenceUploadLimit));
  }, [
    characterSwapActive,
    editImageFiles.length,
    editImagePreviews,
    styleReferenceActive,
    styleReferenceUploadLimit,
  ]);

  useEffect(() => {
    if (!characterSwapActive || !selectedCharacter || !promptTextareaRef.current) {
      return;
    }
    window.setTimeout(() => {
      if (promptTextareaRef.current) {
        promptTextareaRef.current.focus();
      }
    }, 80);
  }, [characterSwapActive, selectedCharacter]);

  useEffect(() => {
    if (!imageMentionOptions.length && imageMentionOpen) {
      closeImageMentionMenu();
    }
  }, [imageMentionOpen, imageMentionOptions.length]);

  const getImageMentionRange = (value, caretPosition) => {
    const beforeCaret = String(value || '').slice(0, caretPosition);
    const atIndex = beforeCaret.lastIndexOf('@');
    if (atIndex === -1) {
      return null;
    }

    const typedQuery = beforeCaret.slice(atIndex + 1);
    if (/\s/.test(typedQuery)) {
      return null;
    }

    return {
      start: atIndex,
      end: caretPosition,
    };
  };

  const updateImageMentionMenu = (value, caretPosition) => {
    if (!imageMentionOptions.length) {
      closeImageMentionMenu();
      return;
    }

    const range = getImageMentionRange(value, caretPosition);
    if (range) {
      setImageMentionOpen(true);
      setActiveImageMentionIndex(0);
    } else {
      closeImageMentionMenu();
    }
  };

  const handlePromptInputChange = (event) => {
    const nextPrompt = event.target.value;
    const caretPosition = event.target.selectionStart || nextPrompt.length;
    updatePrompt(nextPrompt);
    updateImageMentionMenu(nextPrompt, caretPosition);
  };

  const insertImageMention = (option) => {
    if (!option) {
      return;
    }

    const textarea = promptTextareaRef.current;
    const currentPrompt = prompt;
    const caretPosition = textarea && Number.isFinite(textarea.selectionStart)
      ? textarea.selectionStart
      : currentPrompt.length;
    const range = getImageMentionRange(currentPrompt, caretPosition) || {
      start: caretPosition,
      end: caretPosition,
    };
    const insertion = `[${option.label}]`;
    const trailingSpace = currentPrompt.slice(range.end, range.end + 1) === ' ' ? '' : ' ';
    const nextPrompt = `${currentPrompt.slice(0, range.start)}${insertion}${trailingSpace}${currentPrompt.slice(range.end)}`;
    const nextCaretPosition = range.start + insertion.length + trailingSpace.length;

    updatePrompt(nextPrompt);
    closeImageMentionMenu();

    window.setTimeout(() => {
      if (promptTextareaRef.current) {
        promptTextareaRef.current.focus();
        promptTextareaRef.current.setSelectionRange(nextCaretPosition, nextCaretPosition);
      }
    }, 0);
  };

  const handlePromptKeyDown = (event) => {
    if (!imageMentionOpen || !imageMentionOptions.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveImageMentionIndex((current) => (
        current + 1 >= imageMentionOptions.length ? 0 : current + 1
      ));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveImageMentionIndex((current) => (
        current <= 0 ? imageMentionOptions.length - 1 : current - 1
      ));
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      insertImageMention(imageMentionOptions[activeImageMentionIndex]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeImageMentionMenu();
    }
  };

  const handlePromptClick = (event) => {
    updateImageMentionMenu(event.target.value, event.target.selectionStart || 0);
  };

  const resetDescribeState = () => {
    setDescribeFile(null);
    setDescribePreview('');
    setDescribeError('');
    setDescribeResult('');
    setIsDragActive(false);
  };

  const handleCloseDescribeModal = () => {
    if (isDescribeLoading) {
      return;
    }
    setDescribeModalOpen(false);
    resetDescribeState();
  };

  const handleImageSelection = (file) => {
    if (!file) {
      resetDescribeState();
      return;
    }

    setDescribeFile(file);
    setDescribePreview(URL.createObjectURL(file));
    setDescribeError('');
    setDescribeResult('');
    setIsDragActive(false);
  };

  const handleDescribeRequest = async () => {
    if (!describeFile || isDescribeLoading) {
      if (!describeFile) {
        setDescribeError('Select an image to continue');
      }
      return;
    }

    setIsDescribeLoading(true);
    setDescribeError('');
    setDescribeResult('');

    try {
      const { data } = await aiApi.describe({ file: describeFile });
      const generatedPrompt = ((data && data.prompt) || '').trim();

      if (generatedPrompt) {
        setDescribeResult(generatedPrompt);
        message.success('Description ready. Review it before applying.');
      } else {
        message.warning('Description not available for this image');
      }
    } catch (error) {
      const responseMessage =
        (error && error.response && error.response.data && error.response.data.message)
        || (error && error.message)
        || 'Image description failed. Please try again.';
      setDescribeError(responseMessage);
      message.error(responseMessage);
    } finally {
      setIsDescribeLoading(false);
    }
  };

  const handleApplyDescribeResult = () => {
    const nextPrompt = describeResult.trim();
    if (!nextPrompt) {
      setDescribeError('Description cannot be empty.');
      return;
    }

    updatePrompt(nextPrompt);
    message.success('Added description to your prompt');
    setDescribeModalOpen(false);
    resetDescribeState();
  };

  const describeFooter = [
    <Button key="cancel" onClick={handleCloseDescribeModal} disabled={isDescribeLoading}>
      Cancel
    </Button>,
    <Button
      key="describe"
      onClick={handleDescribeRequest}
      loading={isDescribeLoading}
      disabled={!describePreview || isDescribeLoading}
    >
      Describe image
    </Button>,
    <Button
      key="apply"
      type="primary"
      onClick={handleApplyDescribeResult}
      disabled={!describeResult.trim() || isDescribeLoading}
    >
      Use prompt
    </Button>,
  ];

  return (
    <section className="generate-panel">
      {!characterSwapActive && (
        <div className="prompt-card">
          <label htmlFor="prompt-textarea" className="prompt-label">
            Describe the image and art style you have in mind
          </label>
          <textarea
            ref={promptTextareaRef}
            id="prompt-textarea"
            placeholder="Describe the image and art style you have in mind"
            value={prompt}
            onChange={handlePromptInputChange}
            onKeyDown={handlePromptKeyDown}
            onClick={handlePromptClick}
            onBlur={() => {
              window.setTimeout(closeImageMentionMenu, 120);
            }}
          />
          {imageMentionOpen && imageMentionOptions.length ? (
            <div className="image-mention-menu" role="listbox" aria-label="Reference images">
              {imageMentionOptions.map((option, index) => (
                <button
                  key={`${option.label}-${option.previewUrl || index}`}
                  type="button"
                  className={`image-mention-menu__item${
                    index === activeImageMentionIndex ? ' is-active' : ''
                  }`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insertImageMention(option);
                  }}
                  role="option"
                  aria-selected={index === activeImageMentionIndex}
                >
                  {option.previewUrl ? (
                    <img src={option.previewUrl} alt={option.label} />
                  ) : (
                    <span className="image-mention-menu__placeholder">
                      <i className="fa-regular fa-image" aria-hidden="true" />
                    </span>
                  )}
                  <span>
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          <div className="prompt-footer">
            <div className="prompt-meta">
              <span className={`prompt-char-count${promptOverLimit ? ' is-over-limit' : ''}`}>
                {`${prompt.length} / ${selectedPromptMaxCharacters.toLocaleString()} characters`}
              </span>
              <span className={`prompt-helper${promptOverLimit ? ' prompt-helper--warning' : ''}`}>
                {promptOverLimit
                  ? `${selectedModelLabel} supports up to ${selectedPromptMaxCharacters.toLocaleString()} characters.`
                  : 'Enhance Prompt will stay within the selected model limit.'}
              </span>
            </div>
            <Button
              size="large"
              onClick={handleEnhancePrompt}
              loading={isEnhancingPrompt}
              disabled={
                !trimmedPrompt
                || isGenerating
                || isEnhancingPrompt
                || promptOverLimit
                || enhancePromptLocked
              }
            >
              Enhance Prompt
            </Button>
          </div>
        </div>
      )}
      {referenceImageModel && characterSwapActive && (
        <div className="prompt-card">
          <div className="prompt-label-row">
            <label>Character Swap</label>
          </div>
          <label htmlFor="character-swap-prompt-textarea" className="prompt-label">
            Describe the target scene or edit
          </label>
          <textarea
            ref={promptTextareaRef}
            id="character-swap-prompt-textarea"
            placeholder="Describe the target scene or edit"
            value={prompt}
            onChange={handlePromptInputChange}
            onKeyDown={handlePromptKeyDown}
          />
          <div className="prompt-footer prompt-footer--compact">
            <div className="prompt-meta">
              <span className={`prompt-char-count${promptOverLimit ? ' is-over-limit' : ''}`}>
                {`${prompt.length} / ${selectedPromptMaxCharacters.toLocaleString()} characters`}
              </span>
              <span className={`prompt-helper${promptOverLimit ? ' prompt-helper--warning' : ''}`}>
                {promptOverLimit
                  ? `${selectedModelLabel} supports up to ${selectedPromptMaxCharacters.toLocaleString()} characters.`
                  : 'The selected character and target image stay visible below.'}
              </span>
            </div>
            <Button
              size="large"
              onClick={handleEnhancePrompt}
              loading={isEnhancingPrompt}
              disabled={
                !trimmedPrompt
                || isGenerating
                || isEnhancingPrompt
                || promptOverLimit
                || enhancePromptLocked
              }
            >
              Enhance Prompt
            </Button>
          </div>
          <div className="character-swap-grid">
            <section className="character-swap-box">
              <div className="prompt-label-row">
                <label>Reference Character</label>
              </div>
              <div
                className={`describe-dropzone${selectedCharacterPreviewAsset ? ' has-image' : ''}`}
                role="button"
                tabIndex={0}
                onClick={onOpenCharacterPicker}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (onOpenCharacterPicker) {
                      onOpenCharacterPicker();
                    }
                  }
                }}
              >
                {selectedCharacterPreviewAsset ? (
                  <div className="edit-image-single">
                    <img src={selectedCharacterPreviewAsset.url} alt={selectedCharacter.displayName || 'Saved character'} />
                    <button
                      type="button"
                      className="edit-image-remove"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (onClearCharacterSelection) {
                          onClearCharacterSelection();
                        }
                      }}
                      aria-label="Clear selected character"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="character-swap-box__icon" aria-hidden="true">
                      <i className="fa-regular fa-user" />
                    </div>
                    <p>Select Character</p>
                  </>
                )}
              </div>
              <p className="prompt-helper">
                Choose one saved character to swap into the target image.
              </p>
              <Button
                type="link"
                className="character-swap-box__link"
                onClick={onCreateCharacter}
              >
                Upload a new character
              </Button>
              {selectedCharacter && selectedCharacter.swapStatus === 'blocked' ? (
                <p className="prompt-helper prompt-helper--warning">
                  <strong>Character Swap unavailable.</strong>{' '}
                  {getCharacterSwapBlockedMessage(selectedCharacter.swapBlockedReason)}
                </p>
              ) : null}
            </section>
            <section className="character-swap-box">
              <div className="prompt-label-row">
                <label>Reference Image</label>
              </div>
              <label
                htmlFor="character-swap-reference-image-input"
                className={`describe-dropzone${editImagePreviews.length ? ' has-image' : ''}${
                  isEditDragActive ? ' is-dragover' : ''
                }`}
                role="button"
                tabIndex={0}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!isEditDragActive) {
                    setIsEditDragActive(true);
                  }
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsEditDragActive(false);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsEditDragActive(false);
                  const { files } = event.dataTransfer;
                  if (files && files.length) {
                    handleEditFileSelect([files[0]]);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (editFileInputRef.current) {
                      editFileInputRef.current.click();
                    }
                  }
                }}
              >
                {editImagePreviews.length ? (
                  <div className="edit-image-single">
                    <img src={editImagePreviews[0]} alt="Reference preview" />
                    <button
                      type="button"
                      className="edit-image-remove"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleRemoveEditImage(0);
                      }}
                      aria-label="Remove reference image"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <>
                    <i className="fa-regular fa-image" />
                    <p>Drag & drop image</p>
                    <span>or click to browse</span>
                  </>
                )}
                <input
                  type="file"
                  id="character-swap-reference-image-input"
                  accept="image/*"
                  ref={editFileInputRef}
                  className="describe-file-input"
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    const { files } = event.target;
                    handleEditFileSelect(files ? [files[0]] : []);
                    event.target.value = '';
                  }}
                />
              </label>
              <p className="prompt-helper">
                Upload exactly one target image. Character Swap keeps that image&apos;s scene and swaps in the selected character.
              </p>
            </section>
          </div>
        </div>
      )}
      {referenceImageModel && !characterSwapActive && styleReferenceActive && (
        <div className="prompt-card">
          <div className="prompt-label-row">
            <label>Reference image</label>
            {styleReferenceSummary ? (
              <span className="edit-file-label">{styleReferenceSummary}</span>
            ) : null}
          </div>
          <div className={`style-reference-row${characterSwapModel ? ' has-character-slot' : ''}`}>
            {characterSwapModel ? (
              <button
                type="button"
                className={`reference-tile reference-tile--character${
                  hasCharacterStyleReference ? ' has-image is-selected' : ''
                }`}
                onClick={handleSelectCharacterReference}
              >
                {hasCharacterStyleReference ? (
                  <>
                    <img
                      src={selectedCharacterPreviewAsset.url}
                      alt={selectedCharacter.displayName || 'Character reference'}
                    />
                    <span
                      role="button"
                      tabIndex={0}
                      className="edit-image-remove"
                      onClick={handleClearCharacterReference}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          handleClearCharacterReference(event);
                        }
                      }}
                      aria-label="Clear character reference"
                    >
                      ×
                    </span>
                  </>
                ) : (
                  <>
                    <i className="fa-regular fa-user" />
                    <p>Select character</p>
                    <span>Character ID</span>
                  </>
                )}
              </button>
            ) : null}
            {referenceImageSlots.map((slot, index) => (
                <label
                  key={`reference-image-slot-${index}`}
                  htmlFor="generate-reference-image-input"
                  className={`reference-tile reference-tile--image${
                    slot.previewUrl ? ' has-image is-selected' : ''
                  }${isEditDragActive ? ' is-dragover' : ''}`}
                  role="button"
                  tabIndex={0}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!isEditDragActive) {
                      setIsEditDragActive(true);
                    }
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsEditDragActive(false);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsEditDragActive(false);
                    const { files } = event.dataTransfer;
                    if (files && files.length) {
                      handleGenerateReferenceFileSelect(files);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      if (editFileInputRef.current) {
                        editFileInputRef.current.click();
                      }
                    }
                  }}
                >
                  {slot.previewUrl ? (
                    <>
                      <img src={slot.previewUrl} alt={`Source preview ${index + 1}`} />
                      <button
                        type="button"
                        className="edit-image-remove"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleRemoveEditImage(index);
                        }}
                        aria-label={`Remove source image ${index + 1}`}
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <>
                      <i className="fa-regular fa-image" />
                      <p>Image {index + 1}</p>
                      <span>Click or drop</span>
                    </>
                  )}
                </label>
            ))}
            <input
              type="file"
              id="generate-reference-image-input"
              accept="image/*"
              ref={editFileInputRef}
              className="describe-file-input"
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                const { files } = event.target;
                handleGenerateReferenceFileSelect(files);
                event.target.value = '';
              }}
              multiple
            />
          </div>
          <p className="prompt-helper">
            {`Style Reference supports up to ${styleReferenceUploadLimit} uploaded image${styleReferenceUploadLimit === 1 ? '' : 's'}${hasCharacterStyleReference ? ' with your selected character' : ''} for blending. Use @ in the prompt to insert [Image 1], [Image 2], and so on.`}
          </p>
        </div>
      )}
      <div className="control-shelf">
        <Tooltip title="Let the assistant help refine your idea">
          <Button
            className="control-chip"
            icon={<i className="fa-solid fa-wand-magic-sparkles" />}
            onClick={() => {
              setDescribeModalOpen(true);
              resetDescribeState();
            }}
          >
            Try Design Assistant
          </Button>
        </Tooltip>
        <Dropdown menu={aspectRatioMenu} trigger={['click']}>
          <Button className="control-chip" icon={<i className="fa-solid fa-expand" />}>
            {selectedSizePrefix} · {selectedSizeLabel}
          </Button>
        </Dropdown>
        <Dropdown menu={stylesMenu} trigger={['click']}>
          <Button className="control-chip" icon={<i className="fa-solid fa-palette" />}>
            {selectedStyle
              ? `Style: ${
                  (() => {
                    const foundStyle = STYLE_OPTIONS.find((s) => s.value === selectedStyle);
                    return (foundStyle && foundStyle.label) || 'Custom';
                  })()
                }`
              : 'Styles'}
          </Button>
        </Dropdown>
        <Dropdown menu={modelMenu} trigger={['click']}>
          <Button className="control-chip" icon={<i className="fa-solid fa-robot" />}>
            AI Models: {selectedModelDisplayLabel}
          </Button>
        </Dropdown>
        <Button
          className={`control-chip${styleReferenceActive ? ' is-active' : ''}`}
          icon={<i className="fa-regular fa-clone" />}
          onClick={handleStyleReferenceClick}
          disabled={!referenceImageModel}
        >
          Style Reference
        </Button>
        <Button
          className={`control-chip${characterSwapActive ? ' is-active' : ''}`}
          icon={<i className="fa-solid fa-people-arrows" />}
          onClick={handleCharacterSwapClick}
          disabled={!characterSwapModel}
        >
          Character Swap
        </Button>
        <div className="generate-action">
          <Button
            type="primary"
            size="large"
            className="generate-btn"
            icon={<GenerateSparklesIcon />}
            onClick={handleGenerateClick}
            loading={isGenerating}
            disabled={!canGenerate}
          >
            Generate
          </Button>
          <span className="control-chip__hint control-chip__hint--block">
            This generation deducts {formatModelCredits(selectedModelCreditCost) || '(1 credit)'} per image.
          </span>
        </div>
      </div>
      <Modal
        open={describeModalOpen}
        title="Design Assistant"
        onCancel={handleCloseDescribeModal}
        destroyOnHidden
        footer={describeFooter}
      >
        <p>Upload an image and let the assistant describe it for you.</p>
        <label
          htmlFor="design-assistant-image-input"
          className={`describe-dropzone${describePreview ? ' has-image' : ''}${
            isDragActive ? ' is-dragover' : ''
          }`}
          role="button"
          tabIndex={0}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!isDragActive) {
              setIsDragActive(true);
            }
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragActive(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragActive(false);
            const { files } = event.dataTransfer;
            if (files && files.length) {
              handleImageSelection(files[0]);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              if (describeFileInputRef.current) {
                describeFileInputRef.current.click();
              }
            }
          }}
        >
          {describePreview ? (
            <img src={describePreview} alt="Uploaded preview" />
          ) : (
            <>
              <i className="fa-regular fa-image" />
              <p>Drag & drop image</p>
              <span>or click to browse</span>
            </>
          )}
          <input
            type="file"
            id="design-assistant-image-input"
            accept="image/*"
            ref={describeFileInputRef}
            className="describe-file-input"
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              const { files } = event.target;
              if (!files || !files.length) {
                resetDescribeState();
                return;
              }
              handleImageSelection(files[0]);
              event.target.value = '';
            }}
          />
        </label>
        {describeFile ? (
          <p className="describe-selected">
            Selected image: <strong>{describeFile.name}</strong>
          </p>
        ) : null}
        {describeError ? <p className="describe-error">{describeError}</p> : null}
        {describeResult ? (
          <div className="describe-review">
            <label htmlFor="describe-result">Generated prompt preview</label>
            <textarea
              id="describe-result"
              value={describeResult}
              onChange={(event) => setDescribeResult(event.target.value)}
            />
            <div className="describe-review-footer">
              <span className="prompt-char-count">{`${describeResult.length} characters`}</span>
              <span>Review and edit before using.</span>
            </div>
          </div>
        ) : null}
      </Modal>
    </section>
  );
};

GeneratePanel.propTypes = {
  promptValue: PropTypes.string,
  promptVersion: PropTypes.number,
  onPromptChange: PropTypes.func,
  onGenerate: PropTypes.func,
  isGenerating: PropTypes.bool,
  modelValue: PropTypes.string,
  onModelChange: PropTypes.func,
  sizeValue: PropTypes.string,
  onSizeChange: PropTypes.func,
  referenceImageUrls: PropTypes.arrayOf(PropTypes.string),
  selectedCharacter: PropTypes.shape({
    displayName: PropTypes.string,
    runtimeReferenceCount: PropTypes.number,
    coverAvatar: PropTypes.shape({
      url: PropTypes.string,
    }),
    avatarImages: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
      }),
    ),
    uploadedSourceImages: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
      }),
    ),
    runtimeReferenceImages: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
      }),
    ),
    swapStatus: PropTypes.string,
    swapBlockedReason: PropTypes.string,
  }),
  activateCharacterSwap: PropTypes.bool,
  onOpenCharacterPicker: PropTypes.func,
  onCreateCharacter: PropTypes.func,
  onClearCharacterSelection: PropTypes.func,
  generationModels: PropTypes.arrayOf(PropTypes.shape({
    modelKey: PropTypes.string,
    label: PropTypes.string,
    generationCreditCost: PropTypes.number,
    promptMaxCharacters: PropTypes.number,
    category: PropTypes.string,
    isEnabled: PropTypes.bool,
    defaultSize: PropTypes.string,
    supportedSizes: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      promptSuffix: PropTypes.string,
    })),
  })),
};

GeneratePanel.defaultProps = {
  promptValue: '',
  promptVersion: 0,
  onPromptChange: undefined,
  onGenerate: undefined,
  isGenerating: false,
  modelValue: MODEL_OPTIONS[0].value,
  onModelChange: undefined,
  sizeValue: DEFAULT_SIZE,
  onSizeChange: undefined,
  referenceImageUrls: undefined,
  selectedCharacter: null,
  activateCharacterSwap: false,
  onOpenCharacterPicker: undefined,
  onCreateCharacter: undefined,
  onClearCharacterSelection: undefined,
  generationModels: [],
};

export default GeneratePanel;
